import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const DISMISS_KEY = 'pwaInstallDismissed';

const isRunningStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  );
};

const detectPlatform = () => {
  if (typeof window === 'undefined') {
    return { isIos: false };
  }
  const ua = window.navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
  return { isIos };
};

export default function InstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsStandalone(isRunningStandalone());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsDismissed(localStorage.getItem(DISMISS_KEY) === 'true');
    setIsIos(detectPlatform().isIos);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const isAuthRoute = useMemo(
    () => ['/login', '/reset-password'].includes(location.pathname),
    [location.pathname]
  );

  const shouldShow =
    !isAuthRoute &&
    !isDismissed &&
    !isStandalone &&
    ((deferredPrompt && !isIos) || (isIos && !deferredPrompt));

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setIsDismissed(true);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      setIsDismissed(true);
      localStorage.setItem(DISMISS_KEY, 'true');
    }
    setDeferredPrompt(null);
  };

  if (!shouldShow) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-xs space-y-2 rounded-2xl border border-slate-700 bg-slate-950/95 px-4 py-3 text-xs text-slate-200 shadow-[0_20px_40px_-30px_rgba(0,0,0,0.9)] backdrop-blur">
      <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Install Ofoodi POS</p>
      {isIos ? (
        <p>
          On Safari, tap the Share button and choose “Add to Home Screen” for
          faster access.
        </p>
      ) : (
        <p>Install the app for faster access and a full-screen experience.</p>
      )}
      <div className="flex items-center gap-2">
        {!isIos && (
          <button
            type="button"
            onClick={handleInstall}
            className="rounded-full border border-emerald-400 bg-emerald-500/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-950"
          >
            Install
          </button>
        )}
        <button
          type="button"
          onClick={handleDismiss}
          className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-300 hover:text-emerald-100"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
